import { useQuery, useMutation, useQueryClient } from 'react-query';
import { apiService } from '../../../../utils/api';
import type {
  Layer,
  LayerFunctionOption,
  LayerRequest,
  LayerPatchRequest,
} from '../../../../components/compositionLayersTable/types';

// ============================================
// GET /api/v1/instance-type/layer/functions/
// Layer functions are shared across instance types and templates
// ============================================

export const useGetTemplateLayerFunctions = () => {
  return useQuery({
    queryKey: ['layer-functions'],
    queryFn: async () => {
      const { data } = await apiService.get<LayerFunctionOption[]>(
        '/api/v1/instance-type/layer/functions/'
      );
      return data;
    },
    refetchOnWindowFocus: false,
    staleTime: 0,
  });
};

// ============================================
// GET /api/v1/instance-type-template/{id}/layers/
// ============================================

export const useGetTemplateLayers = (templateId: string, enabled: boolean = true) => {
  return useQuery({
    queryKey: ['template-layers', templateId],
    queryFn: async () => {
      const { data } = await apiService.get<Layer[]>(
        `/api/v1/instance-type-template/${templateId}/layers/`
      );
      return data;
    },
    enabled: enabled && !!templateId,
    refetchOnWindowFocus: false,
  });
};

// ============================================
// POST /api/v1/instance-type-template/{id}/layers/
// ============================================

export const useCreateTemplateLayer = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      instanceTypeId,
      data,
    }: {
      instanceTypeId: string;
      data: LayerRequest;
    }) => {
      const response = await apiService.post<Layer>(
        `/api/v1/instance-type-template/${instanceTypeId}/layers/`,
        data
      );
      return response.data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries(['template-layers', variables.instanceTypeId]);
    },
  });
};

// ============================================
// PATCH /api/v1/instance-type-template/{id}/layers/{layer_id}/
// ============================================

export const useUpdateTemplateLayer = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      instanceTypeId,
      layerId,
      data,
    }: {
      instanceTypeId: string;
      layerId: string;
      data: LayerPatchRequest;
    }) => {
      const response = await apiService.patch<Layer>(
        `/api/v1/instance-type-template/${instanceTypeId}/layers/${layerId}/`,
        data
      );
      return response.data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries(['template-layers', variables.instanceTypeId]);
    },
  });
};

// ============================================
// DELETE /api/v1/instance-type-template/{id}/layers/{layer_id}/
// ============================================

export const useDeleteTemplateLayer = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      instanceTypeId,
      layerId,
    }: {
      instanceTypeId: string;
      layerId: string;
    }) => {
      await apiService.delete(
        `/api/v1/instance-type-template/${instanceTypeId}/layers/${layerId}/`
      );
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries(['template-layers', variables.instanceTypeId]);
    },
  });
};
